/**
 * «Reabrir con plantilla» (§8): pasadas las 24 h, WhatsApp sólo deja escribir con una plantilla aprobada.
 * Es una tarjeta en la misma página atada a `?accion=reabrir` (nunca un modal, R2), y la URL NO viaja a la
 * conversación siguiente: la lista copia sólo sus filtros.
 *
 * Una plantilla cuesta (hasta USD 0,08 la de marketing con recargo), así que:
 * - se ve a quién sale (el número completo) y cuánto cuesta antes de apretar;
 * - un doble clic manda UNA: mientras sale, el botón queda deshabilitado, y la clave del envío va atada a
 *   la plantilla y sus variables (se reusa ante un error de red o un 5xx, cambia si cambia algo);
 * - un rechazo del backend dice su motivo y deja el botón deshabilitado hasta cambiar algo; con un 422 se
 *   vuelven a pedir las opciones (un bloqueo nuevo, como una baja, queda pintado por los datos).
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ConversacionDetalle, EnvioManualPublico, OpcionesReapertura, PlantillaReapertura, VariableReapertura } from "../../types.conversaciones";
import type { BotonPlantilla } from "../../types";
import { CajaError, Esqueleto, haceCuanto, llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";
import { CATEGORIA, textoRechazoPlantilla } from "../../logic/textos";
import { usdPorMensaje } from "../../logic/formato";
import { reemplazarVariables } from "../../logic/variables";
import { capitalizar, nuevaClaveCliente } from "../../logic/conversaciones";
import { PreviaWhatsApp } from "../PreviaWhatsApp";

const AYUDA_FUENTE: Record<string, string> = {
  base: "de los contactos con consentimiento",
  campana: "de la última campaña",
  perfil: "de su perfil de WhatsApp",
  marca: "el nombre de la marca",
};

const ayudaDe = (v: VariableReapertura) => AYUDA_FUENTE[v.fuente] ?? `completalo (ejemplo: ${v.ejemplo || "—"})`;

/** Los botones llegan como `unknown[]` en el contrato: la previa sólo dibuja los que tienen forma de botón. */
function botonesDe(b: unknown[]): BotonPlantilla[] {
  return (Array.isArray(b) ? b : []).filter((x): x is BotonPlantilla => !!x && typeof x === "object" && typeof (x as { tipo?: unknown }).tipo === "string");
}

const valoresIniciales = (p: PlantillaReapertura | undefined): Record<number, string> =>
  Object.fromEntries((p?.variables ?? []).map((v) => [v.numero, v.sugerido || ""]));

export function ReabrirConPlantilla({ conversacion: c, ahora, onEnviado, onCargada }: {
  conversacion: ConversacionDetalle;
  ahora: Date;
  onEnviado: (e: EnvioManualPublico) => void;
  /** Llegaron las plantillas y la tarjeta creció (ya pintada): quien la abrió la vuelve a acomodar en la pantalla. */
  onCargada?: () => void;
}) {
  const nombreGrupo = useId();
  const [, setSp] = useSearchParams();
  const [opciones, setOpciones] = useState<OpcionesReapertura | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [elegida, setElegida] = useState("");
  const [valores, setValores] = useState<Record<number, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [rechazadaCon, setRechazadaCon] = useState("");
  const [otro, setOtro] = useState<{ nombre: string; at: string; texto: string } | null>(null);
  const enviandoRef = useRef(false);
  const claveRef = useRef<{ firma: string; clave: string } | null>(null);
  // Lo que el operador vio al abrir la reapertura: si otra persona del equipo escribe en el medio, 409.
  const vistoRef = useRef(c.ultimaSalidaId);
  const elegidaRef = useRef("");
  const vivo = useRef(true);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const cerrar = useCallback(() => {
    setSp((prev) => { const n = new URLSearchParams(prev); n.delete("accion"); return n; }, { replace: true });
  }, [setSp]);

  const cargarOpciones = useCallback(async () => {
    const l = await llamar<OpcionesReapertura>(() => conversacionesApi.opcionesReapertura(c.id));
    if (!vivo.current) return;
    setCargando(false);
    if (!l.ok || !l.data) { setErrorCarga(l.ok ? "El servidor no devolvió las plantillas." : textoError(l)); return; }
    const o = l.data;
    setErrorCarga("");
    setOpciones(o);
    // La elegida se conserva al recargar (con lo que ya escribió el operador); si ya no está, la primera.
    if (!(elegidaRef.current && o.plantillas.some((p) => p.id === elegidaRef.current))) {
      const primera = o.plantillas[0];
      elegidaRef.current = primera?.id ?? "";
      setElegida(elegidaRef.current);
      setValores(valoresIniciales(primera));
    }
  }, [c.id]);

  // También cuando cambia lo que decide el bloqueo: pedidas una sola vez, con la tarjeta abierta la ventana vencía y
  // VENTANA_ABIERTA dejaba «Mandar plantilla» deshabilitado hasta cancelar y volver a abrirla, y una baja por chat no
  // aparecía hasta que el backend rechazaba el envío. Lo escrito se conserva (ver arriba).
  useEffect(() => { void cargarOpciones(); }, [cargarOpciones, c.ventanaAbierta, c.baja.enNoContactar]);

  const onCargadaRef = useRef(onCargada);
  onCargadaRef.current = onCargada;
  const cargada = !!opciones;
  useEffect(() => { if (cargada) onCargadaRef.current?.(); }, [cargada]);

  const plantilla = opciones?.plantillas.find((p) => p.id === elegida);
  const firma = useMemo(
    () => `${elegida}|${JSON.stringify((plantilla?.variables ?? []).map((v) => (valores[v.numero] ?? "").trim()))}`,
    [elegida, plantilla, valores],
  );

  const elegir = (id: string) => {
    elegidaRef.current = id;
    setElegida(id);
    setValores(valoresIniciales(opciones?.plantillas.find((p) => p.id === id)));
    setError("");
    setOtro(null);
  };

  const mandar = async (confirmarDuplicado = false) => {
    if (enviandoRef.current || !plantilla || opciones?.bloqueo) return;
    const clave = claveRef.current?.firma === firma ? claveRef.current.clave : nuevaClaveCliente();
    claveRef.current = { firma, clave };
    enviandoRef.current = true;
    setEnviando(true);
    setError("");
    const variables = Object.fromEntries(plantilla.variables.map((v) => [String(v.numero), (valores[v.numero] ?? "").trim()]));
    const l = await llamar<EnvioManualPublico>(() => conversacionesApi.reabrir(c.id, {
      plantillaId: plantilla.id, variables, claveCliente: clave, vistoHasta: vistoRef.current,
      ...(confirmarDuplicado ? { confirmarDuplicado: true } : {}),
    }));
    enviandoRef.current = false;
    if (!vivo.current) return;
    setEnviando(false);
    if (l.ok) {
      claveRef.current = null;
      setOtro(null);
      if (l.data) onEnviado(l.data);
      cerrar();
      return;
    }
    if (l.code === "OTRO_OPERADOR_RESPONDIO") {
      const o = (l.extra.otro && typeof l.extra.otro === "object" ? l.extra.otro : {}) as Record<string, unknown>;
      setOtro({
        nombre: typeof o.nombre === "string" && o.nombre ? o.nombre : "otra persona",
        at: typeof o.at === "string" ? o.at : "",
        texto: typeof o.texto === "string" ? o.texto : "",
      });
      return;
    }
    setError(textoRechazoPlantilla(l.code) ?? textoError(l));
    // Un rechazo que no se arregla reintentando deja el botón quieto hasta que cambie algo. Un 409
    // (hay otra saliendo), la red o un 5xx, no: se puede volver a apretar con la misma clave.
    if ([403, 404, 422].includes(l.status)) setRechazadaCon(firma);
    if (l.status === 422) void cargarOpciones();
  };

  const bloqueado = !!opciones?.bloqueo || !plantilla || rechazadaCon === firma;
  const botonCls = "h-9 px-4 rounded-xl text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <section className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm text-sm text-slate-700 space-y-3" aria-labelledby={`${nombreGrupo}-titulo`}>
      <div>
        <h3 id={`${nombreGrupo}-titulo`} className="font-semibold text-slate-900">Reabrir con plantilla</h3>
        {/* Sin «pasaron más de 24 h»: acá también se llega con la ventana cerrada por WhatsApp (63016) o por el cambio de
            número, y ahí era falso. */}
        <p className="mt-1">
          Con la ventana de 24 h cerrada, WhatsApp sólo deja escribirle con una plantilla aprobada. Si contesta, la ventana se
          vuelve a abrir y seguís con texto libre.
        </p>
      </div>

      {cargando && !opciones ? (
        <Esqueleto filas={2} alto="h-12" />
      ) : errorCarga && !opciones ? (
        <CajaError titulo="No pudimos traer las plantillas." detalle={errorCarga} onReintentar={() => { setCargando(true); void cargarOpciones(); }} />
      ) : opciones ? (
        <>
          {opciones.bloqueo && <p className="rounded-xl bg-red-50 px-3 py-2 text-red-800">{opciones.bloqueo.texto}</p>}
          {opciones.aviso && <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">{opciones.aviso}</p>}

          {!opciones.plantillas.length ? (
            <p>
              No hay plantillas aprobadas que se puedan usar con esta cuenta.{" "}
              <Link to="/activa/oficial/plantillas/modelos" className="font-semibold text-violet-700 underline">Crear una desde un modelo</Link>
            </p>
          ) : (
            <>
              <fieldset className="space-y-1.5">
                <legend className="sr-only">Plantilla</legend>
                {opciones.plantillas.map((p) => (
                  <label key={p.id} className={`flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 ${p.id === elegida ? "border-violet-300 bg-violet-50" : "border-slate-200"}`}>
                    <input type="radio" name={nombreGrupo} value={p.id} checked={p.id === elegida} onChange={() => elegir(p.id)} />
                    <span className="font-medium text-slate-900">{p.nombre}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {CATEGORIA[p.categoria as keyof typeof CATEGORIA] ?? p.categoria}
                    </span>
                    <span className="text-xs text-slate-500">{usdPorMensaje(p.costoClienteUSD)} por mensaje</span>
                  </label>
                ))}
              </fieldset>

              {plantilla && (
                <>
                  {plantilla.variables.map((v) => (
                    <div key={v.numero}>
                      <label htmlFor={`${nombreGrupo}-v${v.numero}`} className="block text-xs font-semibold text-slate-600">
                        {`{{${v.numero}}}`} · {v.alias || "sin nombre"}
                      </label>
                      <input id={`${nombreGrupo}-v${v.numero}`} value={valores[v.numero] ?? ""}
                        onChange={(e) => { setValores((x) => ({ ...x, [v.numero]: e.target.value })); setError(""); setOtro(null); }}
                        className="mt-1 h-9 w-full rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                      <p className="mt-0.5 text-xs text-slate-500">{ayudaDe(v)}</p>
                    </div>
                  ))}
                  <PreviaWhatsApp compacto botones={botonesDe(plantilla.botones)} mediaUrl={plantilla.mediaUrl ?? undefined}
                    texto={reemplazarVariables(plantilla.cuerpoCompuesto, (n) => (valores[n]?.trim() ? valores[n] : undefined))} />
                  {plantilla.categoria === "MARKETING" && (
                    <p className="text-amber-700">Es de marketing: cuesta más y Meta puede no entregarla si la persona recibió muchas promociones.</p>
                  )}
                  {/* Decisión de Alan (25/09): una promoción reciente se avisa y no frena el envío. Las campañas sí la
                      excluyen por la fatiga de la marca; acá el operador tiene la conversación enfrente y decide. Por eso
                      no entra en `bloqueado`, y sólo con una MARKETING: una de utilidad no es una promoción. */}
                  {/* La regla de la marca (29/10/2026) suma el oficial y los WhatsApp por QR: con K = 1, como siempre; con K > 1,
                      dice cuántos ya le llegaron. `ultimaAt` es null si lo que cuenta llegó sólo por QR. */}
                  {plantilla.categoria === "MARKETING" && opciones.avisoFatiga && (opciones.avisoFatiga.maximo ?? 1) <= 1 && (
                    <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">
                      Esta persona recibió una promoción {haceCuanto(opciones.avisoFatiga.ultimaAt, ahora) || "hace poco"}: se le puede
                      mandar igual. Las campañas (por WhatsApp oficial o por QR) no le mandan otra hasta que pasen {opciones.avisoFatiga.dias}{" "}
                      {opciones.avisoFatiga.dias === 1 ? "día" : "días"} de la última.
                    </p>
                  )}
                  {plantilla.categoria === "MARKETING" && opciones.avisoFatiga && (opciones.avisoFatiga.maximo ?? 1) > 1 && (
                    <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">
                      Esta persona ya recibió {opciones.avisoFatiga.maximo} mensajes en los últimos {opciones.avisoFatiga.dias}{" "}
                      {opciones.avisoFatiga.dias === 1 ? "día" : "días"}, sumando WhatsApp oficial y por QR
                      {opciones.avisoFatiga.ultimaAt ? ` (el último por este número, ${haceCuanto(opciones.avisoFatiga.ultimaAt, ahora)})` : ""}: se le puede
                      mandar igual. Las campañas no le mandan más hasta que vuelva a quedar por debajo de {opciones.avisoFatiga.maximo}.
                    </p>
                  )}
                  <p>
                    Se le manda a <span className="font-mono whitespace-nowrap">{opciones.destino}</span> · {usdPorMensaje(plantilla.costoClienteUSD)}
                  </p>
                </>
              )}
            </>
          )}

          {otro && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900">
              <p>
                {capitalizar(haceCuanto(otro.at, ahora)) || "Recién"} le respondió {otro.nombre}{otro.texto ? <>: «{otro.texto}»</> : null}. ¿Mandás igual tu respuesta?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" onClick={() => void mandar(true)} disabled={enviando} className={`${botonCls} bg-amber-600 text-white`}>Mandar igual</button>
                <button type="button" onClick={() => setOtro(null)} disabled={enviando} className={`${botonCls} border border-amber-200 bg-white`}>No mandar</button>
              </div>
            </div>
          )}
          {error && <p role="alert" className="text-red-700">{error}</p>}
        </>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => void mandar()} disabled={enviando || bloqueado}
          className={`${botonCls} bg-violet-600 text-white hover:bg-violet-700`}>
          {enviando ? <><Loader2 size={14} className="animate-spin" /> Mandando…</> : "Mandar plantilla"}
        </button>
        <button type="button" onClick={cerrar} disabled={enviando} className={`${botonCls} border border-slate-200 bg-white`}>Cancelar</button>
      </div>
    </section>
  );
}

export default ReabrirConPlantilla;
